// @flow
import { Field } from "redux-form";
import type { FieldProps } from "redux-form";
import * as React from "react";
import { Header, Grid, Popup, Input, Icon } from "semantic-ui-react";
import { Trans } from "react-i18next";

import GridRowReusable from "../SubComponents/GridRowReusable";
import RadioButtonReusable from "../SubComponents/RadioButtonReusable";
import { ConsumptionButtons } from "../../../../Commons/Components/redux-form-components/ConsumptionButtons";
import GoogleTagManager from "../../../../Commons/Utilities/GoogleTagManager";
import { InputWithError } from "../../../../Commons/Components/redux-form-components/InputWithError";
import Styles from "../ShortOfferForm.module.scss";
import { CategoryTypes, fromSlug } from "../../../../Commons/Modules/category";
import { RFDynamicSelectProvider, RFDynamicSelectTariffs } from "../SubComponents/ReduxFormComponents";

import type { ReminderT } from "../../../Reminder/misc/Types";
import type { CategorySlugT } from "../../../../Commons/Modules/category";
import {
  isNumberGreaterThan,
  isNumberSmallerThan,
  isNumber,
  isPresent,
  isNumberWithoutDots,
} from "../../../../Commons/Utilities/validators";
import { maxConsumptionGas, maxConsumptionStrom } from "../../../../Commons/Utilities/Constants/MaxConsumption";

export const RadioBtnsEcoTariff = (props: {
  t: typeof TFunction,
  name: string,
  native?: boolean,
  unfolding?: boolean,
  no: string,
  yes: string,
  forceMobile?: boolean,
  tags?: Object,
}) => {
  const { t, name, native, unfolding, no, yes, forceMobile, tags: gtm } = props;
  return (
    <GridRowReusable
      leftCompact={true}
      headerString={
        unfolding
          ? t("blogArticleUnfolding:formFieldDescriptors.ecoTariff.description")
          : t("formFieldDescriptors.ecoTariff.description")
      }
      forceMobile={forceMobile}
    >
      <Field
        name={name}
        key={name + "_field"}
        component={({ input }: FieldProps): React.Node => {
          return (
            <RadioButtonReusable
              labels={[t("formFieldDescriptors.ecoTariff.no"), t("formFieldDescriptors.ecoTariff.yes")]}
              values={[no, yes]}
              input={input}
              inline={!native}
              tags={gtm || null}
            />
          );
        }}
      />
    </GridRowReusable>
  );
};

export const ConsumptionField = (props: {
  name: string,
  t: typeof TFunction,
  categoryType: ?string,
  customStyles?: Dict | string[],
  native: boolean,
  launchEvent?: boolean,
  change?: ?Function,
  unfolding?: boolean,
  forceMobile?: boolean,
  propertyType?: string,
}) => {
  const { t, name, categoryType, customStyles, native, launchEvent, unfolding, forceMobile, propertyType } = props;
  const maxConsumption = categoryType === CategoryTypes.ELECTRICITY ? maxConsumptionStrom : maxConsumptionGas;
  return (
    <>
      {unfolding && (
        <Header as={"h3"} textAlign={"center"} style={{ marginTop: "2rem" }}>
          {t("blogArticleUnfolding:formFieldDescriptors.electricityYearlyConsumption.description")}
        </Header>
      )}
      <GridRowReusable
        leftCompact={true}
        headerString={t("formFieldDescriptors.electricityYearlyConsumption.description")}
        forceMobile={forceMobile}
      >
        <Grid.Row>
          <Field
            key={"consumption_buttons"}
            name={name}
            validate={[isNumber, isNumberSmallerThan(maxConsumption), isNumberGreaterThan(0)]}
            component={ConsumptionButtons}
            forceMobile={forceMobile}
            categoryType={categoryType}
            customStyles={customStyles}
            onChange={() => {
              if (launchEvent) {
                GoogleTagManager.consumptionGiven();
              }
            }}
            props={{
              propertyType,
            }}
            native={native}
          />
        </Grid.Row>
        <Grid.Row>
          <Field
            name={name}
            key={name + "_field"}
            validate={[
              isPresent,
              isNumber,
              isNumberWithoutDots,
              isNumberGreaterThan(0),
              isNumberSmallerThan(maxConsumption),
            ]}
            placeholder={t(`formFieldDescriptors.${name}.label`)}
            data-cy={"consumption-input"}
            fluid
            label={{ content: "kWh" }}
            onBlur={(event) => {
              if (launchEvent) {
                GoogleTagManager.consumptionGiven();
              }
            }}
            labelPosition="right"
            component={InputWithError}
          />
        </Grid.Row>
      </GridRowReusable>
    </>
  );
};

type PSFProps = {
  name: string,
  name2: string,
  postalCode?: number,
  locationId?: number,
  categorySlug: ?CategorySlugT,
  change: Function,
  t: typeof TFunction,
  providersLoading: boolean,
  messageOnTop?: boolean,
  providersLoaded: boolean,
  textAlign?: string,
  native: boolean,
  unfolding?: boolean,
  reminderToBeSent?: ?ReminderT,
  forceMobile?: boolean,
  isCompany?: boolean,
  gtm?: Object,
};

export const ProviderSelectField = (props: PSFProps) => {
  let {
    name,
    name2,
    t,
    postalCode,
    locationId,
    categorySlug,
    change,
    providersLoading,
    providersLoaded,
    messageOnTop,
    native,
    unfolding,
    forceMobile,
    isCompany,
    gtm,
  } = props;
  return (
    <>
      <GridRowReusable
        leftCompact={true}
        headerString={
          !unfolding
            ? t("formFieldDescriptors.currentProvider.description")
            : t("blogArticleUnfolding:formFieldDescriptors.currentProvider.label")
        }
        forceMobile={forceMobile}
      >
        {messageOnTop && (
          <div className={Styles["mini-text-above-field"]}>
            {providersLoaded ? (
              <div data-cy="providersNoteLoaded">
                <Trans i18nKey="blogArticleUnfolding:formFieldDescriptors.currentProvider.providersNoteLoaded">
                  <p>First part</p>
                  Second part
                </Trans>
              </div>
            ) : (
              t("formFieldDescriptors.currentProvider.providersNoteDefault")
            )}
          </div>
        )}
        <Field
          name={name}
          validate={[isPresent]}
          key={name + "_field"}
          search
          fluid
          loading={providersLoading}
          placeholder={t("formFieldDescriptors.currentProvider.label")}
          postalCode={postalCode}
          locationId={locationId}
          categoryType={fromSlug(categorySlug)}
          change={change}
          props={{
            isCompany,
            gtm: gtm?.provider,
          }}
          component={RFDynamicSelectProvider}
        />
      </GridRowReusable>
      <GridRowReusable
        leftCompact={true}
        headerString={
          native ? (
            <div
              style={{
                display: "inline",
                whiteSpace: "nowrap",
              }}
            >
              {unfolding
                ? t("blogArticleUnfolding:formFieldDescriptors.currentTariff.descriptionNative")
                : t("formFieldDescriptors.currentTariff.descriptionNative")}
              <Popup
                trigger={<Icon name="info circle" size="large" />}
                wide
                content={t("formFieldDescriptors.currentTariff.descriptionNativeTooltip")}
                className="semantic-tooltip-style"
                position="top center"
                on={["click", "hover", "focus"]}
              />
            </div>
          ) : unfolding ? (
            t("blogArticleUnfolding:formFieldDescriptors.currentTariff.label")
          ) : (
            t("formFieldDescriptors.currentTariff.description")
          )
        }
        forceMobile={forceMobile}
      >
        <Field
          name={name2}
          validate={[isPresent]}
          key={name2 + "_field"}
          search
          fluid
          loading={providersLoading}
          placeholder={t("formFieldDescriptors.currentTariff.label")}
          postalCode={postalCode}
          locationId={locationId}
          categoryType={fromSlug(categorySlug)}
          change={change}
          props={{ gtm: gtm?.tariff }}
          component={RFDynamicSelectTariffs}
        />
      </GridRowReusable>
    </>
  );
};

export const RadioBtnsOnBehalfOf = (props: {
  t: typeof TFunction,
  name: string,
  native: boolean,
  ME: string,
  FAMILY_FRIENDS: string,
  OTHERS: string,
  forceMobile?: boolean,
}) => {
  const { t, name, native, ME, FAMILY_FRIENDS, OTHERS, forceMobile } = props;
  return (
    <GridRowReusable headerString={t("formFieldDescriptors.onBehalfOf.description")} forceMobile={forceMobile}>
      <Field
        key={name + "_field"}
        name={name}
        component={({ input }: FieldProps): React.Node => {
          return (
            <RadioButtonReusable
              labels={[
                t("formFieldDescriptors.onBehalfOf.options.ME"),
                t("formFieldDescriptors.onBehalfOf.options.FAMILY_FRIENDS"),
                t("formFieldDescriptors.onBehalfOf.options.OTHERS"),
              ]}
              values={[ME, FAMILY_FRIENDS, OTHERS]}
              input={input}
              inline={!native}
            />
          );
        }}
      />
    </GridRowReusable>
  );
};

export const InputOnBehalfOfTitle = (props: { t: typeof TFunction, name: string }) => {
  const { t, name } = props;
  return (
    <GridRowReusable headerString={t("formFieldDescriptors.onBehalfOfTitle.description")}>
      <Field
        key={name + "_field"}
        name={name}
        placeholder={t("formFieldDescriptors.onBehalfOfTitle.placeholder")}
        fluid
        data-cy="onBehalfOfTitle"
        component={Input}
      />
    </GridRowReusable>
  );
};
